import AppHeader from "@/layouts/AppHeader";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { Badge } from "@/components/ui/badge";
import { Reservation, ReservationsResponse } from "@/lib/type";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronsDownIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import WhatsAppFab from "@/components/WhatsAppFab";
import BookingConfirmation from "@/components/BookingConfirmation";
import { AppFooter } from "@/layouts/AppFooter";

function MyReservation() {
  const { slug: accessToken } = useParams<{ slug: string }>(); // Get the slug from the URL
  const [reservations, setReservations] = useState<Reservation>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    fetchReservations();
  }, [accessToken]);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.API_URL}reservations/access/${accessToken}`
      );
      const data = await response.json();
      console.log({ data });
      if (response.ok) {
        setReservations(data.reservations[0]);
      } else {
        throw new Error(data.message || "Failed to fetch reservations");
      }
    } catch (err) {
      // setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelReservation = async (reservationId: string): Promise<void> => {
    try {
      const response = await fetch(
        `${process.env.API_URL}reservations/access/${accessToken}/cancel/${reservationId}`,
        { method: "POST" }
      );

      if (response.ok) {
        toast({
          title: "Reservation cancelled successfully",
          description: "Your reservation has been successfully cancelled.",
        });
        fetchReservations();
      } else {
        setIsErrorOpen(true);

        const errorObj = await response.json();
        setErrorMessage(errorObj.message);
        throw new Error("Failed to cancel reservation");
      }
    } catch (err: any) {
      toast({
        title: "Uh oh! Something went wrong.",
        description: err.message,
        action: <ToastAction altText="Try again">Try again</ToastAction>,
      });
    }
  };

  const renderGridInfo = (data: Record<string, any>, level = 0) => {
    const excludedKeys = ["pre_authorization", "MessageType"];

    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 level-${level}`}>
        {Object.entries(data)
          .filter(([key]) => !excludedKeys.includes(key))
          .map(([key, value], index) => {
            return (
              <div key={key} className="flex flex-col pb-2">
                <Label className="font-bold capitalize mb-4">
                  {key.replace(/_/g, " ")}:
                </Label>
                {typeof value === "object" && value !== null ? (
                  // Check for arrays and objects separately
                  Array.isArray(value) ? (
                    <div className="ml-4">
                      {value.map((item, index) => (
                        <div key={index} className="border-t mt-2 pt-2">
                          {renderGridInfo(item, level + 1)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="ml-4">
                      {renderGridInfo(value, level + 1)}
                    </div>
                  )
                ) : (
                  <Input
                    id={`${value}-${level}=${index}`}
                    className="pointer-events-none"
                    defaultValue={value || "-"}
                    placeholder={value || "-"}
                    onChange={() => {}}
                  />
                )}
              </div>
            );
          })}
      </div>
    );
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <WhatsAppFab />

      <AppHeader
        className="bg-neutral-100 pb-4 px-4 md:px-32 relative w-full z-30"
        textColor="text-black"
        borderColor="border-black"
      />
      <div className="p-6 md:mx-32 mx-4 my-12">
        <h1 className="text-xl font-bold mb-4">My Reservations</h1>

        <div>
          {!!!reservations ? (
            <div>No reservations found.</div>
          ) : (
            <div className="overflow-y-auto py-2 mx-auto">
              <BookingConfirmation reservation={reservations} />
              {/* if status is pending, and confirmed, show the cancel button */}
              {(reservations.status === "pending" || reservations.status === "confirmed") && (
                <Dialog open={isOpen} onOpenChange={setIsOpen}>
                  <DialogTrigger asChild>
                    <Button variant="destructive" className="mt-2">
                    Cancel Reservation
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto p-6 mx-auto ">
                  <DialogTitle>Are you sure you want to cancel?</DialogTitle>
                  <DialogFooter>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        cancelReservation(reservations.reservation_id);
                        setIsOpen(!isOpen);
                      }}
                    >
                      Yes
                    </Button>
                    <Button
                      variant="default"
                      onClick={() => setIsOpen(!isOpen)}
                    >
                      No
                    </Button>
                  </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}

              <Dialog open={isErrorOpen} onOpenChange={setIsErrorOpen}>
                <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto p-6 mx-auto ">
                  <DialogTitle>
                    {errorMessage ?? "Failed to cancel reservation"}
                  </DialogTitle>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>
      </div>
      <AppFooter />
    </>
  );
}

export default MyReservation;
